import output from "_src/engine/output.js";
import objFactory from "_src/engine/factories/object";
import {blendEquation, pixelBufferType} from "_src/engine/constants";
import bufferFactory from "_src/engine/factories/pixel_buffer";
import events from "_src/engine/events";
import { geometry, easingType, EPSILON } from "_src/engine/constants";
import { angle2D } from "_src/engine/math";
import Animation from "_src/client/animation";

import { vec2, vec3, quat } from "_src/libs/gl-matrix";

import DefaultShader from "_src/shaders/default/default_shader";
import windowManager from "_src/client/window_manager";
import renderGraph from "_src/render_graph/render_graph";
import loader from "_src/loader";
import { screenUtils, mouseUtils } from "_src/client/utils";
import controlUtils from "_src/client/controls/utils";
import inputCollector from "_src/client/input";
import { getPositionByMinmax, calcOrthoCameraFrustum } from "_src/utils";
import appState from "_src/app_state";


import viewcubePng from "_assets/textures/viewcube.png";
import edgeBillboardPng from "_assets/textures/edge_billboard.png";
import faceBillboardPng from "_assets/textures/face_billboard.png";
import vertextBillboardPng from "_assets/textures/vertex_billboard.png";
import rotationArrowPng from "_assets/textures/rotation_arrow.png";

import viewcubeObj from "_assets/meshes/viewcube.obj";
import {colorType} from "_src/constants";

const PICK_MOVE_DISTANCE = 5;
const BILLBOARD_OPACITY = 0.1;
const ROTATION_OBJECT_BASE_OPACITY = 0.7;
const ROTATION_OBJECT_SELECTED_OPACITY = 1.0;

const VIEWCUBE_OBJECT_ID = 6;
const ROTATION_OBJECT_CW_ID = 17;
const ROTATION_OBJECT_CCW_ID = 18;
const LABEL_X_OBJECT_ID = 1;
const LABEL_Y_OBJECT_ID = 0;
const LABEL_Z_OBJECT_ID = 2;

const SELECT_EDGE_Z_ID = 13;
const SELECT_VERTEX_OBJECT_ID = 14;

const LABEL_AXIS_OFFSET = 0.0;

const VERTEX_TYPE = 0;
const EDGE_TYPE_X = 1;
const EDGE_TYPE_Y = 2;
const EDGE_TYPE_Z = 3;
const FACE_TYPE = 4;

const ANGLE_SPEED = 0.5 / Math.PI;

class Navigation {
  constructor(type, position, angles) {
    this.type = type;
    this.position = position;
    this.angles = angles;
  }
}

let navigationTable = null;
let currentSelectId = -1;
let selectedRotationArrowId = -1;
let animation = null;
let pressPositionX = -1;
let pressPositionY = -1;
let lockState = false;
let _enabled = true;

export default {

  rightOffset : 0,
  topOffset : 0,
  width : 1,
  height : 1,

  async init() {

    createNavigationTable();
    animation = new Animation();
    const mainRenderGraph = windowManager.getWindow(windowManager.MAIN).renderGraph;

    mainRenderGraph.setEnabled(renderGraph.VIEWCUBE, _enabled);

    const colorTexturePromise = createTexture(viewcubePng);
    const billboardEdgeTexturePromise = createTexture(edgeBillboardPng);
    const billboardFaceTexturePromise = createTexture(faceBillboardPng);
    const billboardVertexTexturePromise = createTexture(vertextBillboardPng);
    const rotationTexturePromise = createTexture(rotationArrowPng);

    let objectsData;
    try {
      objectsData = await loader.load(viewcubeObj, loader.EXTERNAL);
    } catch(error) {
      if (!loader.isLegalError(error)) {
        output.error(output.DEVELOPERS_ERROR, "Can't load viewcube data. " + error);
      }

      return;
    }

    const viewCubePass = mainRenderGraph.getPass(renderGraph.VIEWCUBE);
    const viewCubeScene = viewCubePass.getMainScene();
    const billboardScene = viewCubePass.getScenes()[2];
    const camera = viewCubeScene.camera;

    const viewCubeshader = new DefaultShader({
      transparent: false,
      lighting: false,
      colorMode: colorType.TEXTURE_UV,
      specularColor: [0,0,0],
    });

    const axesShader = new DefaultShader({
      transparent: false,
      lighting: false,
      colorMode: colorType.TEXTURE_UV,
      specularColor: [0,0,0],
    });

    const billboardShader = new DefaultShader( {
      transparent: true,
      lighting: false,
      colorMode: colorType.TEXTURE_UV,
      depthTest: false,
    });
    billboardShader.setBlendEquation(blendEquation.FUNC_ADD, blendEquation.MAX);

    const rotationShader = new DefaultShader( {
      transparent: true,
      lighting: false,
      colorMode: colorType.TEXTURE_UV,
      screenSpace: true,
      depthTest: false,
    });
    rotationShader.setBlendEquation(blendEquation.FUNC_ADD, blendEquation.MAX);

    let navigationPos = 0;
    for (let i = 0; i < ROTATION_OBJECT_CW_ID; ++i) {
      const geom = objectsData[i].geometry;

      if (i > VIEWCUBE_OBJECT_ID) {
        let repeatCount;
        let texture;
        if (i<SELECT_EDGE_Z_ID) {
          repeatCount = 1;
          texture = await billboardFaceTexturePromise;
        } else if (i === SELECT_VERTEX_OBJECT_ID) {
          repeatCount = 8;
          texture = await billboardVertexTexturePromise;
        } else {
          repeatCount = 4;
          texture = await billboardEdgeTexturePromise;
        }

        for (let j=0; j<repeatCount; j++) {
          const billboard = objFactory.createDrawable(billboardShader);
          billboard.setOpacity(BILLBOARD_OPACITY);
          billboard.addGeometryChunk(geom);
          const pos = navigationTable[navigationPos++].position;
          billboard.setTranslation(pos[0], pos[1], pos[2]);
          billboard.setTexture(texture, 0);
          billboardScene.add(billboard);
          billboard.setVisibility(false);
        }
        continue;
      }

      const shader = i === VIEWCUBE_OBJECT_ID ? viewCubeshader : axesShader;

      const viewcubePart = objFactory.createDrawable(shader);
      viewcubePart.addGeometryChunk(geom);
      viewcubePart.setTexture(await colorTexturePromise, 0);

      if (i===LABEL_X_OBJECT_ID) {
        viewcubePart.setTranslation(LABEL_AXIS_OFFSET,  -1.5, -1.5);
      }
      if (i===LABEL_Y_OBJECT_ID) {
        viewcubePart.setTranslation( -1.5, LABEL_AXIS_OFFSET, -1.5);
      }
      if (i===LABEL_Z_OBJECT_ID) {
        viewcubePart.setTranslation( -1.5,  -1.5,  LABEL_AXIS_OFFSET);
      }

      viewCubeScene.add(viewcubePart);
    }

    const rotationTexture = await rotationTexturePromise;
    const cwRotationModel = objFactory.createDrawable(rotationShader);
    const rotationCWGeometry = objectsData[ROTATION_OBJECT_CW_ID].geometry;
    cwRotationModel.addGeometryChunk(rotationCWGeometry);
    cwRotationModel.setTexture(rotationTexture, 0);
    cwRotationModel.setOpacity(ROTATION_OBJECT_BASE_OPACITY);
    cwRotationModel.setVisibility(false);

    const ccwRotationModel = objFactory.createDrawable(rotationShader);
    const rotationCCWGeometry = objectsData[ROTATION_OBJECT_CCW_ID].geometry;
    ccwRotationModel.addGeometryChunk(rotationCCWGeometry);
    ccwRotationModel.setTexture(rotationTexture, 0);
    ccwRotationModel.setOpacity(ROTATION_OBJECT_BASE_OPACITY);
    ccwRotationModel.setVisibility(false);

    billboardScene.add(cwRotationModel);
    billboardScene.add(ccwRotationModel);

    this.updateRotationArrowsAppearance();
    this.setScreenAppearance(this.rightOffset, this.topOffset, this.width, this.height);
    this.update(camera);

    events.on("client_mousemove", onMouseMove);
    events.on("client_mouseup",   onMouseUp);
    events.on("client_mousedown", onMouseDown);

  },

  update: (function() {

    const _VEC3_TMP = vec3.create();
    const _X_DIR = vec3.fromValues(1, 0, 0 );
    const _Z_DIR = vec3.fromValues(0, 0, 1 );

    const _QUAT_TMP1 = quat.create();
    const _QUAT_TMP2 = quat.create();
    const _QUAT_TMP3 = quat.create();

    const _VEC2_TMP = vec2.create();
    const _VEC2_TMP2 = vec2.create();

    return function(camera) {

      const mainRenderGraph = windowManager.getWindow(windowManager.MAIN).renderGraph;

      const viewCubePass = mainRenderGraph.getPass(renderGraph.VIEWCUBE);
      const mainScene = viewCubePass.getMainScene();
      const billboardScene = viewCubePass.getScenes()[2];
      const viewCubeCamera = mainScene.camera;
      const billboardCamera = billboardScene.camera;

      const camDir = vec3.set(_VEC3_TMP, 0, 0, 3);
      vec3.transformQuat(camDir, camDir, camera.quat);

      const quatZ = quat.identity(_QUAT_TMP1);
      const xDirXY = vec2.set(_VEC2_TMP, _X_DIR[0], _X_DIR[1]);
      const camDirProjectedXY = vec2.set(_VEC2_TMP2, camDir[0], camDir[1]);
      quat.rotateZ(quatZ, quatZ, angle2D(xDirXY, camDirProjectedXY));

      const quatX = quat.identity(_QUAT_TMP2);
      const zDirYZ = vec2.set(_VEC2_TMP, _Z_DIR[1], _Z_DIR[2]);
      const camDirProjectedYZ = vec2.set(_VEC2_TMP2, camDir[1], camDir[2]);
      quat.rotateX(quatX, quatX, angle2D(zDirYZ, camDirProjectedYZ));

      const quatY = quat.identity(_QUAT_TMP3);
      const camDirProjectedXZ = vec2.set(_VEC2_TMP2, camDir[0], camDir[2]);
      quat.rotateY(quatY, quatY, -angle2D(zDirYZ, camDirProjectedXZ));

      viewCubeCamera.copyRotation(camera.quat);
      viewCubeCamera.copyTranslation(camDir);

      billboardCamera.copyRotation(camera.quat);
      billboardCamera.copyTranslation(camDir);

      const labelX = mainScene.getChildren()[LABEL_X_OBJECT_ID];
      const labelY = mainScene.getChildren()[LABEL_Y_OBJECT_ID];
      const labelZ = mainScene.getChildren()[LABEL_Z_OBJECT_ID];

      if (!labelX || !labelY || !labelZ) {
        return;
      }

      for (let i = 0; i < navigationTable.length; i++) {
        const navigation = navigationTable[i];
        const billboard = billboardScene.getChildren()[i];
        switch (navigation.type) {
          case VERTEX_TYPE: {
            billboard.copyRotation(camera.quat);
            break;
          }
          case EDGE_TYPE_X: {
             billboard.copyRotation(quatX);
             break;
          }
          case EDGE_TYPE_Y: {
            billboard.copyRotation(quatY);
            break;
          }
          case EDGE_TYPE_Z: {
            billboard.copyRotation(quatZ);
            break;
          }

          default: {
            break;
          }
        }
      }

      labelY.copyRotation(camera.quat);
      labelX.copyRotation(camera.quat);
      labelZ.copyRotation(camera.quat);
    }

  })(),

  setEnabled(enabled) {
    const mainWindow = windowManager.getWindow(windowManager.MAIN);
    if (mainWindow) {
      const mainRenderGraph = mainWindow.renderGraph;
      mainRenderGraph.setEnabled(renderGraph.VIEWCUBE, enabled);
    }
    _enabled = enabled;
  },

  setScreenAppearance(rightOffset, topOffset, width, height) {
    const mainWindow = windowManager.getWindow(windowManager.MAIN);
    if (mainWindow) {
      const mainRenderGraph = mainWindow.renderGraph;
      const viewCubePass = mainRenderGraph.getPass(renderGraph.VIEWCUBE);
      viewCubePass.setScreenAppearance(rightOffset, topOffset, width, height);
    }
    this.rightOffset = rightOffset;
    this.topOffset = topOffset;
    this.width = width;
    this.height = height;
  },

  getScreenAppearance() {
    return [this.rightOffset, this.topOffset, this.width, this.height];
  },

  updateAnimation(dt) {
    if (animation) {
      animation.update(dt);
    }
  },

  updateRotationArrowsAppearance() {
    const enabled = appState.getStateValue("cameraControl") === appState.CC_TRACKBALL;
    const billboardScene = windowManager.getWindow(windowManager.MAIN).renderGraph.getPass(renderGraph.VIEWCUBE).getScenes()[2];
    const billboardSceneLength = billboardScene.getChildren().length;
    const cwRotationModel = billboardScene.getChildren()[billboardSceneLength - 2];
    const ccwRotationModel = billboardScene.getChildren()[billboardSceneLength - 1];
    cwRotationModel.setVisibility(enabled);
    ccwRotationModel.setVisibility(enabled);
  },

  cleanup() {
    events.off("client_mousemove", onMouseMove);
    events.off("client_mouseup",   onMouseUp);
    events.off("client_mousedown", onMouseDown);
    navigationTable = null;
    animation = null;
  }

}

function createNavigationTable() {

  navigationTable = [
    //fixed orientation, no need to translate
    new Navigation(FACE_TYPE,   [ 0,  0,  0], [ Math.PI,      -Math.PI/2]),
    new Navigation(FACE_TYPE,   [ 0,  0,  0], [-Math.PI/2,     0        ]),
    new Navigation(FACE_TYPE,   [ 0,  0,  0], [ 0,             Math.PI/2]),
    new Navigation(FACE_TYPE,   [ 0,  0,  0], [ Math.PI/2,     0        ]),
    new Navigation(FACE_TYPE,   [ 0,  0,  0], [ 0,             0        ]),
    new Navigation(FACE_TYPE,   [ 0,  0,  0], [ Math.PI,       0        ]),

    new Navigation(EDGE_TYPE_Z, [-1, -1,  0], [ 3*Math.PI/4,   0        ]),
    new Navigation(EDGE_TYPE_Z, [-1,  1,  0], [ Math.PI/4,     0        ]),
    new Navigation(EDGE_TYPE_Z, [ 1, -1,  0], [-3*Math.PI/4,   0        ]),
    new Navigation(EDGE_TYPE_Z, [ 1,  1,  0], [-Math.PI/4,     0        ]),

    new Navigation(VERTEX_TYPE, [-1, -1, -1], [ 3*Math.PI/4,  -Math.PI/4]),
    new Navigation(VERTEX_TYPE, [-1, -1,  1], [ 3*Math.PI/4,   Math.PI/4]),
    new Navigation(VERTEX_TYPE, [-1,  1, -1], [ Math.PI/4,    -Math.PI/4]),
    new Navigation(VERTEX_TYPE, [-1,  1,  1], [ Math.PI/4,     Math.PI/4]),
    new Navigation(VERTEX_TYPE, [ 1, -1, -1], [-3*Math.PI/4,  -Math.PI/4]),
    new Navigation(VERTEX_TYPE, [ 1, -1,  1], [-3*Math.PI/4,   Math.PI/4]),
    new Navigation(VERTEX_TYPE, [ 1,  1, -1], [-Math.PI/4,    -Math.PI/4]),
    new Navigation(VERTEX_TYPE, [ 1,  1,  1], [-Math.PI/4,     Math.PI/4]),

    new Navigation(EDGE_TYPE_X, [ 0, -1, -1], [ Math.PI,      -Math.PI/4]),
    new Navigation(EDGE_TYPE_X, [ 0, -1,  1], [ Math.PI,       Math.PI/4]),
    new Navigation(EDGE_TYPE_X, [ 0,  1, -1], [ 0,            -Math.PI/4]),
    new Navigation(EDGE_TYPE_X, [ 0,  1,  1], [ 0,             Math.PI/4]),

    new Navigation(EDGE_TYPE_Y, [-1,  0, -1], [ Math.PI/2,    -Math.PI/4]),
    new Navigation(EDGE_TYPE_Y, [-1,  0,  1], [ Math.PI/2,     Math.PI/4]),
    new Navigation(EDGE_TYPE_Y, [ 1,  0, -1], [-Math.PI/2,    -Math.PI/4]),
    new Navigation(EDGE_TYPE_Y, [ 1,  0,  1], [-Math.PI/2,     Math.PI/4]),
  ];
}

const onMouseMove = (function () {
  const _VEC3_TMP = vec3.create();
  const _VEC3_TMP2 = vec3.create();
  const _VEC3_TMP3 = vec3.create();

  return function (e) {
    if (lockState || !_enabled) {
      return;
    }

    const coords = inputCollector.getCoords();
    const screenX = coords[0];
    const screenY = coords[1];

    const mainWindow = windowManager.getWindow(windowManager.MAIN);
    const viewCubePass = mainWindow.renderGraph.getPass(renderGraph.VIEWCUBE);
    const billboardScene = viewCubePass.getScenes()[2];
    const billboardCamera = billboardScene.camera;
    const {x, y} = screenUtils.toScreen(mainWindow.getViewport(), billboardCamera.viewport, screenX, screenY);

    const previousRotationArrowId = selectedRotationArrowId;
    intersectRotationArrows(x, y);
    if (previousRotationArrowId !== selectedRotationArrowId) {
      const billboardSceneLength = billboardScene.getChildren().length;
      const cwRotationModel = billboardScene.getChildren()[billboardSceneLength - 2];
      const ccwRotationModel = billboardScene.getChildren()[billboardSceneLength - 1];
      const cwOpacity = selectedRotationArrowId === ROTATION_OBJECT_CW_ID ? ROTATION_OBJECT_SELECTED_OPACITY : ROTATION_OBJECT_BASE_OPACITY;
      const ccwOpacity = selectedRotationArrowId === ROTATION_OBJECT_CCW_ID ? ROTATION_OBJECT_SELECTED_OPACITY : ROTATION_OBJECT_BASE_OPACITY;
      cwRotationModel.setOpacity(cwOpacity);
      ccwRotationModel.setOpacity(ccwOpacity);
      mainWindow.renderGraph.requestFrame(false);
    }
    if (selectedRotationArrowId !== -1) return;

    const origin = billboardCamera.calculateOrigin(x, y, _VEC3_TMP);

    const ray = billboardCamera.calculateRay(x, y, _VEC3_TMP2);
    const point = _VEC3_TMP3;

    let distance = Infinity;
    let billboardId = -1;

    for (let i = 0; i < navigationTable.length; i++) {
      const billboard = billboardScene.getChildren()[i];
      if (!billboard) continue;

      if (billboard.castRay(origin, ray, point)) {

        const dist = vec3.distance(point, origin);

        if (dist < distance) {

          distance = dist;
          billboardId = i;
        }
      }
    }

    if (mouseUtils.isLeftButtonPressed(e) && pressPositionY >= 0 && billboardId >= 0) {
      if (Math.sqrt(Math.pow(screenX - pressPositionX, 2) + Math.pow(screenY - pressPositionY, 2)) > PICK_MOVE_DISTANCE) {
        const clientCoords = inputCollector.getClientCoords();
        events.emit("lock_cursor", { x : clientCoords[0], y : clientCoords[1] });
        lockState = true;
        billboardId = -1;
      }
    }

    if (billboardId !== currentSelectId) {
      if (currentSelectId >= 0) {
        billboardScene.getChildren()[currentSelectId].setVisibility(false);
      }
      if (billboardId >= 0) {
        billboardScene.getChildren()[billboardId].setVisibility(true);
      }
      currentSelectId = billboardId;
      mainWindow.renderGraph.requestFrame(false);
    }
  }
})();

function onMouseDown(e) {
  if (e.button !== 0 && currentSelectId < 0 || !_enabled) {
    return;
  }

  const coords = inputCollector.getCoords();
  pressPositionX = coords[0];
  pressPositionY = coords[1];
}

const onMouseUp = (function() {

  const _vec3Tmp = vec3.create();
  const _vec3Tmp2 = vec3.create();
  const _vec3Tmp3 = vec3.create();
  const _vec3Tmp4 = vec3.create();
  const _quatTmp = quat.create();

  return function(e) {

    if (e.button !== 0 || !_enabled) {
      return;
    }

    pressPositionX = -1;
    pressPositionY = -1;

    if (lockState) {
      events.emit("unlock_cursor");
      lockState = false;
      return;
    }

    if (animation.checkBusy()) {
      return;
    }

    const cameraControl = windowManager.getWindow(windowManager.MAIN).cameraControl;
    const camera = cameraControl.object;

    if (!cameraControl.enabled) {
      return;
    }

    if (selectedRotationArrowId !== -1) {
      let angle = Math.PI / 24;
      if (selectedRotationArrowId === ROTATION_OBJECT_CCW_ID) angle *= -1;

      const camDir = vec3.set(_vec3Tmp, 0, 0, 3);
      vec3.normalize(camDir, vec3.transformQuat(camDir, camDir, camera.quat));
      const rotation = quat.setAxisAngle(quat.create(), camDir, angle);

      const startQuat = quat.clone(camera.quat);
      const finalQuat = quat.multiply(quat.create(), rotation, startQuat);

      cameraControl.setEnabled(false);

      animation.type = easingType.IN_OUT_SINE;
      animation.setOnUpdate((value) => {
        const dq = quat.setAxisAngle(_quatTmp, camDir, angle * value);
        quat.multiply(camera.quat, dq, startQuat);
        camera.copyRotation(camera.quat);
        events.emit("camera_move", camera);
        windowManager.getWindow(windowManager.MAIN).renderGraph.requestFrame();
      });

      animation.setOnFinish(() => {
        camera.copyRotation(finalQuat);

        cameraControl.setEnabled(true);
        events.emit("camera_move", camera);
        const mainWindow = windowManager.getWindow(windowManager.MAIN);
        mainWindow.bufferReader.dropBuffers();
        events.emit("visibility_changed", {camera: camera});
        mainWindow.renderGraph.requestFrame();
      });

      animation.start(0, 1, 0.2);

    } else if (currentSelectId >= 0) {
      const minmax = windowManager.getWindow(windowManager.MAIN).getMinMax();
      const target = vec3.set(_vec3Tmp4, 0.5 * (minmax[0] + minmax[3]),
                                         0.5 * (minmax[1] + minmax[4]),
                                         0.5 * (minmax[2] + minmax[5]));

      const startDist = cameraControl.dist;
      const startTarget = vec3.clone(cameraControl.target);
      const [oldHor, oldVer] = controlUtils.anglesFromViewOriented(cameraControl.direction);
      const [newHor, newVer] = navigationTable[currentSelectId].angles;

      const newView = controlUtils.viewFromAngles(vec3.create(), newHor, newVer);
      const quatHor = quat.setAxisAngle(quat.create(), cameraControl.upVec, newHor);
      const newRight = vec3.transformQuat(vec3.create(), geometry.AXIS_MX, quatHor);
      const objUp = vec3.cross(vec3.create(), newRight, newView);

      const finalQuat = quat.setAxes(quat.create(), newView, newRight, objUp);
      quat.conjugate(finalQuat, finalQuat);

      cameraControl.setEnabled(false);

      const rect = {minx : -1, maxx : 1, miny : -1, maxy : 1};
      const eye = getPositionByMinmax(finalQuat, camera.fov, camera.aspect, minmax, rect, target, _vec3Tmp);
      const distance = vec3.distance(target, eye);

      const startHeight = camera.top;
      const {height, width} = calcOrthoCameraFrustum(finalQuat, minmax, rect);
      const finalHeight = height * camera.aspect > width ? height : width / camera.aspect;

      let horDelta = newHor - oldHor;
      if (horDelta > Math.PI) {
        horDelta -= 2 * Math.PI;
      }
      if (horDelta < -Math.PI) {
        horDelta += 2 * Math.PI;
      }

      const startQuat = quat.clone(camera.quat);
      const invQuat = quat.invert(quat.create(), camera.quat);
      const deltaQuat = quat.multiply(quat.create(), finalQuat, invQuat);
      const unitQuat = quat.create();

      const vertDelta = newVer - oldVer;
      if (Math.abs(horDelta) == Math.PI && Math.abs(vertDelta) < 10 * EPSILON) {
        quat.setAxisAngle(deltaQuat, geometry.AXIS_Z, horDelta);
      }

      animation.type = easingType.IN_OUT_SINE;

      animation.setOnUpdate((value) => {

        const dq = quat.slerp(invQuat, unitQuat, deltaQuat, value);
        quat.multiply(camera.quat, dq, startQuat);

        const dist = startDist + value * (distance - startDist);
        const currTarget = vec3.lerp(_vec3Tmp2, startTarget, target, value);

        const hor = oldHor + value * horDelta;
        const ver = oldVer + value * (newVer - oldVer);

        const eye = controlUtils.perspectiveTransFromTargetAngles(hor, ver, currTarget, dist, _vec3Tmp3);

        const height = startHeight + value * (finalHeight - startHeight);
        const width = height * camera.aspect;

        camera.top = height;
        camera.bottom = -height;
        camera.right = width;
        camera.left = -width;

        camera.copyRotation(camera.quat);
        camera.copyTranslation(eye);

        events.emit("camera_move", camera);
        windowManager.getWindow(windowManager.MAIN).renderGraph.requestFrame();

      });

      animation.setOnFinish(() => {

        const [hor, ver] = controlUtils.anglesFromViewOriented(newView);
        const eye = controlUtils.perspectiveTransFromTargetAngles(hor, ver, target, distance, _vec3Tmp3);

        camera.copyRotation(finalQuat);
        camera.copyTranslation(eye);

        cameraControl.setEnabled(true);
        cameraControl.dist = distance;
        cameraControl.lookAt(target[0], target[1], target[2]);

        cameraControl.setPosition(eye);
        cameraControl.updateRotation();

        camera.top = finalHeight;
        camera.bottom = -finalHeight;
        camera.right = finalHeight * camera.aspect;
        camera.left = -finalHeight * camera.aspect;

        events.emit("camera_move", camera);
        const mainWindow = windowManager.getWindow(windowManager.MAIN);
        mainWindow.bufferReader.dropBuffers();
        events.emit("visibility_changed", {camera: camera});
        mainWindow.renderGraph.requestFrame();
      });

      const angleTime = Math.max(Math.abs(horDelta), Math.abs(vertDelta)) * ANGLE_SPEED;
      const time = Math.max(angleTime, 0.2);
      animation.start(0, 1, time);
    }

  }

})();

function createTexture(path) {
  return loader.load(path, loader.IMAGE).then((image) => {

    if (!image) {
      return null;
    }

    const texture = bufferFactory.create(pixelBufferType.T_RGBA,
      {width: image.width, height: image.height, flipY: true,
        anisotropy: 16, immutable: true});

    texture.initMipMapLevelImage(0, image);
    return texture;

  }).catch((error) => {
    if (!loader.isLegalError(error)) {
      output.error(output.DEVELOPERS_ERROR, "Can't load viewcube texture. " + error);
    }
  });
}

function intersectRotationArrows(x, y) {
  const isInsideBbox = (x, y, bbox) => {
    return x >= bbox[0] && x <= bbox[3] && y >= bbox[1] && y <= bbox[4];
  };

  const billboardScene = windowManager.getWindow(windowManager.MAIN).renderGraph.getPass(renderGraph.VIEWCUBE).getScenes()[2];
  const billboardSceneLength = billboardScene.getChildren().length;
  const cwRotationModel = billboardScene.getChildren()[billboardSceneLength - 2];
  const ccwRotationModel = billboardScene.getChildren()[billboardSceneLength - 1];
  if (cwRotationModel.checkIsHidden() || ccwRotationModel.checkIsHidden()) {
    selectedRotationArrowId = -1;
    return;
  }

  const cwBbox = cwRotationModel.chunks[0].geometry.bbox;
  const ccwBbox = ccwRotationModel.chunks[0].geometry.bbox;
  if (isInsideBbox(x, y, cwBbox)) {
    selectedRotationArrowId = ROTATION_OBJECT_CW_ID;
  } else if (isInsideBbox(x, y, ccwBbox)) {
    selectedRotationArrowId = ROTATION_OBJECT_CCW_ID;
  } else {
    selectedRotationArrowId = -1;
  }
}


